import axios from 'axios';
import config from '../config';

const codeManagerApi = axios.create({
    baseURL: config.codeManagerApiUrl,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
    },
    withCredentials: true
});

// Add request interceptor for authentication
codeManagerApi.interceptors.request.use(async request => {
    try {
        const token = localStorage.getItem('authToken');
        if (token) {
            request.headers['Authorization'] = `Bearer ${token}`;
        }
    } catch (error) {
        console.error('CodeManager API - Request error:', error);
    }
    return request;
});

// Response interceptor for error handling
codeManagerApi.interceptors.response.use(
    response => {
        console.log('CodeManager API - Success:', {
            url: response.config.url,
            method: response.config.method,
            status: response.status,
            data: response.data
        });
        return response;
    },
    error => {
        console.error('CodeManager API - Error:', {
            url: error.config?.url,
            method: error.config?.method,
            status: error.response?.status,
            statusText: error.response?.statusText,
            message: error.response?.data?.error || error.message,
            data: error.response?.data,
            headers: error.config?.headers,
            timestamp: new Date().toISOString(),
            stack: error.stack
        });
        return Promise.reject(error);
    }
);

// Get available paid codes for selected period
export const getPaidCodes = async (valid_from, valid_until) => {
    try {
        const response = await codeManagerApi.get('/api/codes/paid', {
            params: {
                valid_from,
                valid_until
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching paid codes:', error);
        throw error;
    }
};

// Check availability only for complete codes
export const checkCodeAvailability = async ({ code, valid_from, valid_until, exclude_code_id }) => {
    try {
        const response = await codeManagerApi.post('/api/codes/check-availability', {
            code,
            valid_from,
            valid_until,
            exclude_code_id
        });
        return response.data;
    } catch (error) {
        console.error('Error checking code availability:', error);
        throw error;
    }
};

// Get owner's codes
export const getOwnerCodes = async () => {
    try {
        const response = await codeManagerApi.get('/api/codes/owner');
        return response.data;
    } catch (error) {
        console.error('Error fetching owner codes:', error);
        throw error;
    }
};

// Create new code
export const createCode = async (codeData) => {
    try {
        const formattedData = {
            ...codeData,
            valid_from: codeData.valid_from ? new Date(codeData.valid_from).toISOString() : null,
            valid_until: codeData.valid_until ? new Date(codeData.valid_until).toISOString() : null
        };
        
        const response = await codeManagerApi.post('/api/codes', formattedData);
        return response.data;
    } catch (error) {
        console.error('Failed to create code:', error);
        throw error;
    }
};

// Update existing code
export const updateCode = async (codeId, codeData) => {
    try {
        const formattedData = {
            ...codeData,
            valid_from: codeData.valid_from ? new Date(codeData.valid_from).toISOString() : null,
            valid_until: codeData.valid_until ? new Date(codeData.valid_until).toISOString() : null
        };
        
        const response = await codeManagerApi.put(`/api/codes/${codeId}`, formattedData);
        return response.data;
    } catch (error) {
        console.error('Failed to update code:', error);
        throw error;
    }
};

// Delete code
export const deleteCode = async (codeId) => {
    try {
        await codeManagerApi.delete(`/api/codes/${codeId}`);
        return true;
    } catch (error) {
        console.error('Failed to delete code:', error);
        throw error;
    }
};

// Validate URL
export const validateUrl = async (url) => {
    try {
        const response = await codeManagerApi.post('/api/codes/validate-url', { url });
        return response.data;
    } catch (error) {
        console.error('Error validating URL:', error);
        throw error;
    }
};

// Add content moderation check
export const checkContentModeration = async (content) => {
    try {
        const response = await codeManagerApi.post('/api/codes/check-content', content);
        return response.data;
    } catch (error) {
        console.error('Error checking content:', error);
        throw error;
    }
};

// Add image upload function
export const uploadImage = async (imageFile, existingUrl) => {
    const formData = new FormData();
    formData.append('image', imageFile);
    
    // If we have existing URL, extract the path
    if (existingUrl) {
        const path = existingUrl.split('.com/')[1];
        formData.append('existing_path', path);
    }

    try {
        const response = await codeManagerApi.post('/api/upload/image', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
        // Return just the URL string
        return response.data.imageUrl;
    } catch (error) {
        console.error('Error uploading image:', error);
        throw error;
    }
};

// Get click statistics
export const getClickStats = async ({ endDate, fromDate }) => {
    try {
        const params = {
            end_date: endDate.toISOString(),
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
        };
        
        if (fromDate) {
            params.from_date = fromDate.toISOString();
        }
        
        const response = await codeManagerApi.get('/api/stats/clicks', { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching click stats:', error);
        throw error;
    }
};

// First, let's look at the working endpoints
export const checkEmailExists = async (email) => {
    const response = await codeManagerApi.post('/api/auth/owner/check-email', { email });
    return response.data;
};

export const loginCodeOwner = async (email, password) => {
    const response = await codeManagerApi.post('/api/auth/owner/login', { email, password });
    return response.data;
};

// Now let's add ToS endpoints in the same pattern
export const getCurrentToS = async () => {
    const response = await codeManagerApi.get('/api/auth/owner/tos/current');
    return response.data;
};

export const getToSVersions = async () => {
    const response = await codeManagerApi.get('/api/auth/owner/tos/versions');
    return response.data;
};

export const acceptToS = async (version) => {
    const response = await codeManagerApi.post('/api/auth/owner/tos/accept', { version });
    return response.data;
};

export const checkToSStatus = async () => {
    const response = await codeManagerApi.get('/api/auth/owner/tos/status');
    return response.data;
};

// Add verifyCodeOwnerOTP function
export const verifyCodeOwnerOTP = async (email, otp, tosVersion) => {
    try {
        const response = await codeManagerApi.post('/api/auth/owner/verify-email', {
            email,
            otp,
            tosVersion
        });
        return response.data;
    } catch (error) {
        console.error('OTP verification error:', error);
        throw error;
    }
};

export const getCodeOwnerDetails = async () => {
    try {
        const response = await codeManagerApi.get('/api/codes/owner/details');
        return response.data;
    } catch (error) {
        console.error('Error fetching code owner details:', error);
        throw error;
    }
};

// Add getParameters function
export const getParameters = async () => {
    try {
        const response = await codeManagerApi.get('/api/parameters');
        return response.data;
    } catch (error) {
        console.error('Error fetching parameters:', error);
        throw error;
    }
};

// Add payment-related API calls
export const createPaymentIntent = async (amount, codeValue, reason, codeId) => {
    try {
        const response = await codeManagerApi.post('/payment/create-intent', {
            amount: parseInt(amount, 10),
            codeValue,
            reason,
            codeId
        });
        return response.data;
    } catch (error) {
        console.error('Error creating payment intent:', error);
        throw error;
    }
};

export const getStripeConfig = async () => {
    try {
        const response = await codeManagerApi.get('/api/payment/config');
        return response.data;
    } catch (error) {
        console.error('Error fetching Stripe config:', error);
        throw error;
    }
};

export const createTempCode = async (codeData) => {
    try {
        const response = await codeManagerApi.post('/api/codes/create-temp', codeData);
        return response.data;
    } catch (error) {
        console.error('Error creating temporary code:', error);
        throw error;
    }
};

export const deleteTempCode = async (codeId) => {
    try {
        // Use the regular delete endpoint
        await codeManagerApi.delete(`/api/codes/${codeId}`);
        console.log('Temporary code deleted:', codeId);
    } catch (error) {
        console.error('Error deleting temporary code:', error);
        throw error;
    }
};

export const convertTempCode = async (codeId, paymentIntentId, additionalData = {}) => {
    try {
        console.log('Converting temp code with data:', { codeId, paymentIntentId, additionalData }); // Add logging
        const response = await codeManagerApi.post(`/api/codes/temp/${codeId}/convert`, {
            payment_intent_id: paymentIntentId,
            ...additionalData
        });
        console.log('Conversion response:', response.data); // Add logging
        return response.data;
    } catch (error) {
        console.error('Error converting temp code:', error);
        throw error;
    }
};

// Add code suggestions function
export const getCodeSuggestions = async ({ prefix, valid_from, valid_until }) => {
    try {
        const response = await codeManagerApi.post('/api/codes/suggestions', {
            prefix,
            valid_from,
            valid_until
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching code suggestions:', error);
        throw error;
    }
};

export default codeManagerApi; 